import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3157';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export interface U3157ActiveWorkNature {
  id: string;
  workNatureCode?: string;
  workNatureDescriptionFr?: string;
  workNatureDescriptionEn?: string;
}

/** Réponse GET /U3157/initLU + logicalUnitCode fusionné côté shell. */
export interface U3157InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
  activeWorkNatures?: U3157ActiveWorkNature[];
}

export interface U3157ImportModelSearchRow {
  id: string;
  name?: string;
  workNatureCount?: number;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3157ImportModel {
  id: string;
  name: string;
  workNatureIds: string[];
  version: number;
}

export type U3157ImportModelFormRow = U3157ImportModel;

export interface U3157ImportModelCreateBody {
  name: string;
  workNatureIds: string[];
}

export interface U3157ImportModelUpdateBody {
  id: string;
  name: string;
  workNatureIds: string[];
  version: number;
}

export interface U3157ImportModelDeleteBody {
  id: string;
  version: number;
}

export const U3157_ERROR_CODE_DUPLICATE_MANDATE_TYPE = 'BU315701';
export const U3157_ERROR_CODE_INVALID_WORK_NATURE_IDS = 'BU315702';

export function createEmptyU3157FormRow(): U3157ImportModelFormRow {
  return {
    id: '',
    name: '',
    workNatureIds: [],
    version: 0,
  };
}

export function normalizeU3157FromApi(row: U3157ImportModel): U3157ImportModelFormRow {
  const workNatureIds = Array.isArray(row.workNatureIds) ? [...row.workNatureIds].sort() : [];
  return {
    ...row,
    name: typeof row.name === 'string' ? row.name.trim() : '',
    workNatureIds,
  };
}

export function u3157FormRowToSearchRow(
  row: U3157ImportModel | U3157ImportModelFormRow,
  existing?: U3157ImportModelSearchRow | null,
): U3157ImportModelSearchRow {
  const searchRow: U3157ImportModelSearchRow = {
    id: row.id,
    name: row.name,
    workNatureCount: Array.isArray(row.workNatureIds) ? row.workNatureIds.length : undefined,
    version: row.version,
  };
  if (existing) {
    return { ...existing, ...searchRow };
  }
  return searchRow;
}

export function u3157FormatActiveWorkNatureLabel(nature: U3157ActiveWorkNature, language: string): string {
  const code = nature.workNatureCode?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? nature.workNatureDescriptionFr : nature.workNatureDescriptionEn)?.trim() ||
    nature.workNatureDescriptionFr?.trim() ||
    nature.workNatureDescriptionEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic || nature.id;
}

export function buildCreateBody(data: U3157ImportModelFormRow): U3157ImportModelCreateBody {
  return {
    name: data.name.trim(),
    workNatureIds: [...data.workNatureIds],
  };
}

export function buildUpdateBody(data: U3157ImportModelFormRow): U3157ImportModelUpdateBody {
  return {
    id: data.id,
    name: data.name.trim(),
    workNatureIds: [...data.workNatureIds],
    version: data.version,
  };
}

export function buildDeleteBody(data: U3157ImportModelFormRow): U3157ImportModelDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3157ImportModelCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3157ImportModelUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3157ImportModelDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3157_ERROR_CODE_DUPLICATE_MANDATE_TYPE:
      errorResult.fieldErrors['name'] = t('errMandateTypeCodeDuplicate', { ns: nameSpace });
      break;
    case U3157_ERROR_CODE_INVALID_WORK_NATURE_IDS:
      errorResult.fieldErrors['workNatureIds'] = t('errWorkNatureIdsInvalid', { ns: nameSpace });
      break;
    default:
      break;
  }
}
